// ─── 类型 ──────────────────────────────────────────────────────────────────────

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface UserContext {
  userName?: string;
  userId?: string;
  run_mode?: string;
}

export interface MonsoleOptions {
  /** 是否同步打印到浏览器控制台，默认 true */
  printConsole?: boolean;
  /** 是否上报到云端，默认 true */
  syncCloud?: boolean;
}

// ─── 核心类 ────────────────────────────────────────────────────────────────────

class Monsole {
  private opts: Required<MonsoleOptions> = {
    printConsole: true,
    syncCloud: true,
  };

  private user: Required<UserContext> = {
    userName: '',
    userId: '',
    run_mode: '',
  };

  // ── 初始化 ──────────────────────────────────────────────────────────────────

  /**
   * 初始化日志工具
   * @param userCtx  用户信息（userName / userId / run_mode）
   * @param options  控制台打印 & 云端上报开关
   */
  init(userCtx: UserContext = {}, options: MonsoleOptions = {}): void {
    this.user = { ...this.user, ...userCtx };
    this.opts = { ...this.opts, ...options };
  }

  /** 动态更新用户信息（登录后调用） */
  setUser(userCtx: UserContext): void {
    this.user = { ...this.user, ...userCtx };
  }

  // ── 浏览器信息 ──────────────────────────────────────────────────────────────

  private getBrowserInfo(): Record<string, string> {
    const nav = navigator;
    const scr = screen;
    return {
      userAgent: nav.userAgent,
      url: window.location.href,
      referrer: document.referrer,
      language: nav.language,
      platform: nav.platform,
      screenResolution: `${scr.width}x${scr.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      colorDepth: String(scr.colorDepth),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  // ── 上报 ────────────────────────────────────────────────────────────────────

  private send(level: LogLevel, args: unknown[]): void {
    if (!this.opts.syncCloud) return;

    const content = args
      .map((a) => {
        if (a === null) return 'null';
        if (a === undefined) return 'undefined';
        if (a instanceof Error) {
          return `${a.message}\n${a.stack ?? ''}`;
        }
        if (typeof a === 'object') {
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        }
        return String(a);
      })
      .join(' ');

    const params = new URLSearchParams({
      // 用户信息
      user_name: this.user.userName,
      user_id: this.user.userId,
      run_mode: this.user.run_mode,
      // 浏览器信息
      browser_info: JSON.stringify(this.getBrowserInfo()),
      // 日志内容
      level,
      content,
      timestamp: String(Date.now()),
    });

    // 使用 img 发送日志（无跨域限制，不影响页面运行）
    const img = new Image();
    img.src = `/api/tosgateway/log/record?${params.toString()}`;
  }

  // ── 记录方法 ────────────────────────────────────────────────────────────────

  private record(level: LogLevel, args: unknown[]): void {
    if (this.opts.printConsole) {
      // eslint-disable-next-line no-console
      (console[level] as (...a: unknown[]) => void)(...args);
    }
    this.send(level, args);
  }

  log(...args: unknown[]): void   { this.record('log',   args); }
  info(...args: unknown[]): void  { this.record('info',  args); }
  warn(...args: unknown[]): void  { this.record('warn',  args); }
  error(...args: unknown[]): void { this.record('error', args); }
  debug(...args: unknown[]): void { this.record('debug', args); }

  // ── 全局异常捕获 ────────────────────────────────────────────────────────────

  initErrorCapture(): void {
    // JS 运行时错误
    window.onerror = (message, source, lineno, colno, error) => {
      const location = source ? `${source}:${lineno}:${colno}` : '';
      console.log('[monsole onerror]', message, location, error);
      this.error('[onerror]', message, location, error?.stack ?? error ?? '');
      return false;
    };

    // 未捕获的 Promise rejection
    window.addEventListener('unhandledrejection', (e) => {
      const reason = e.reason;
      const stack = reason instanceof Error ? reason.stack : undefined;
      this.error('[unhandledrejection]', reason, stack ?? '');
    });

    // 资源加载失败（img/script/css），过滤掉已由 onerror 处理的运行时错误
    window.addEventListener('error', (e) => {
      if (Object.prototype.toString.call(e) === '[object Event]') {
        const target = e.target as HTMLElement;
        this.error('[resource error]', target?.tagName, (target as HTMLImageElement)?.src || (target as HTMLScriptElement)?.src || '');
      }
    }, true);
  }
}

// ─── 单例 & 挂载 window ────────────────────────────────────────────────────────

const monsole = new Monsole();

declare global {
  interface Window {
    monsole: Monsole;
  }
}

window.monsole = monsole;

export default monsole;
export { Monsole };
